import { TripLeg } from '../../data/models/reservation.model';
import { SortModel } from '../../data/models/sort-model.model';
import { TripLegEventType } from './updateTripLeg';
import { FilterModel } from '../../data/models/filters.model';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';

const STRING_COLUMNS = [
  'pickUpDueTimeUTC',
  'appointmentTimeUTC',
  'appointmentDate',
  'tripLegReferenceID',
  'tripLegStatusBC',
  'tripLegStatusName',
  'tripLegExceptionBC',
  'tripLegExceptionName',
  'tripLegExceptionReason',
  'tripLegExceptionReasonName',
  'tripLegExceptionReasonName',
];

const BOOLEAN_COLUMNS = ['isEscalated', 'isUnderDiscussion', 'isException'];

class RealtimeUtils {
  private doesTripLegMatchFilters(
    tripLeg: TripLegSearchResponseItem,
    filters: FilterModel,
  ): boolean {
    if (filters?.appointmentStartDate) {
      if (
        tripLeg.appointmentDate &&
        tripLeg?.appointmentDate.split('T')[0] <
          filters.appointmentStartDate.split('T')[0]
      ) {
        return false;
      }
    }

    if (filters?.appointmentEndDate) {
      if (
        tripLeg.appointmentDate &&
        tripLeg?.appointmentDate.split('T')[0] >
          filters.appointmentEndDate.split('T')[0]
      ) {
        return false;
      }
    }

    // TODO: Determine the actual logic for this filter
    // if (filters?.excludeRideshareWillCalls) {
    //   if (tripLeg.isWillCall) {
    //     return false;
    //   }
    // }

    if (filters?.isException) {
      if (!tripLeg.tripLegExceptionBC) {
        return false;
      }
    }

    if (filters?.isUnderDiscussion) {
      if (!tripLeg.isUnderDiscussion) {
        return false;
      }
    }

    if (filters?.isEscalated) {
      if (!tripLeg.isEscalated) {
        return false;
      }
    }

    if (filters?.isInternal) {
      if (!tripLeg.isInternal) {
        return false;
      }
    }

    if (filters?.tripLegExceptionBC) {
      if (
        tripLeg.tripLegExceptionBC &&
        !filters.tripLegExceptionBC.includes(tripLeg.tripLegExceptionBC)
      ) {
        return false;
      }
    }

    return true;
  }

  private isTripLegWithinStringColumnSort(
    tripLeg: TripLegSearchResponseItem,
    firstTripLeg: TripLegSearchResponseItem,
    lastTripLeg: TripLegSearchResponseItem,
    column: keyof TripLegSearchResponseItem,
    direction: string,
    page: number,
    lastPage: number,
  ): boolean {
    if (
      tripLeg[column] !== undefined &&
      firstTripLeg[column] !== undefined &&
      lastTripLeg[column] !== undefined
    ) {
      if (
        direction.toLowerCase() === 'asc' &&
        (tripLeg[column]! < firstTripLeg[column]! ||
          tripLeg[column]! > lastTripLeg[column]!)
      ) {
        if (page === lastPage) {
          return true;
        } else {
          return false;
        }
      } else if (
        direction.toLowerCase() === 'desc' &&
        (tripLeg[column]! > firstTripLeg[column]! ||
          tripLeg[column]! < lastTripLeg[column]!)
      ) {
        if (page === 1) {
          return true;
        } else {
          return false;
        }
      }
    }
    return true;
  }

  private isTripLegWithinBooleanColumnSort(
    tripLeg: TripLegSearchResponseItem,
    firstTripLeg: TripLegSearchResponseItem,
    lastTripLeg: TripLegSearchResponseItem,
    columns: [keyof TripLegSearchResponseItem, keyof TripLegSearchResponseItem],
    direction: string,
    page: number,
    lastPage: number,
  ): boolean {
    if (
      tripLeg.tripLegExceptionBC &&
      (firstTripLeg[columns[0]] || lastTripLeg[columns[0]])
    ) {
      return this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        columns[1],
        direction,
        page,
        lastPage,
      );
    }
    return false;
  }

  private doesTripLegSortToCurrentPage(
    tripLeg: TripLegSearchResponseItem,
    filters: FilterModel,
    tripLegs: TripLegSearchResponseItem[],
  ): boolean {
    // If it matches all the filters, would it appear on the current page?
    const columns = filters.sort.split(',');
    const directions = filters.sortOrder?.split(',');

    // If there's less trip legs than the page size, we can return early
    if (tripLegs.length < filters.pageSize) {
      return true;
    }

    const firstTripLeg = tripLegs[0];
    const lastTripLeg = tripLegs[tripLegs.length - 1];

    let withinFirstSort = false;
    let withinSecondSort = false;

    const lastPage = Math.ceil(tripLegs.length / filters.pageSize);

    if (STRING_COLUMNS.includes(columns?.[0])) {
      const column = columns?.[0] as keyof TripLegSearchResponseItem;
      withinFirstSort = this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        column,
        directions?.[0]!,
        filters.page,
        lastPage,
      );
    } else if (BOOLEAN_COLUMNS.includes(columns?.[0])) {
      const columnA = columns?.[0] as keyof TripLegSearchResponseItem;
      const columnB = columns?.[1] as keyof TripLegSearchResponseItem;
      withinFirstSort = this.isTripLegWithinBooleanColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        [columnA, columnB],
        directions?.[0]!,
        filters.page,
        lastPage,
      );
    }

    if (STRING_COLUMNS.includes(columns?.[1])) {
      const column = columns?.[1] as keyof TripLegSearchResponseItem;
      withinSecondSort = this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        column,
        directions?.[1]!,
        filters.page,
        lastPage,
      );
    }

    return withinFirstSort && withinSecondSort;
  }

  public shouldRefreshTripLegData({
    tripLegEventType,
    tripLegEvent,
    tripLegs,
    allTripLegs,
    filters,
  }: {
    tripLegEventType: TripLegEventType;
    tripLegEvent: any;
    tripLegs: TripLegSearchResponseItem[];
    allTripLegs?: TripLegSearchResponseItem[];
    filters?: FilterModel;
  }): boolean {
    // We can't determine sorting logic without the API response filters
    if (!filters || !filters.sortOrder) {
      return false;
    }

    // If the Trip Leg is on the current page, we need to return true.
    const foundIndex = tripLegs.findIndex(
      (tripLeg) =>
        tripLeg.tripLegReferenceID === tripLegEvent.tripLegReferenceID,
    );
    if (foundIndex >= 0) {
      if (tripLegEventType === TripLegEventType.TripLegEscalated) {
        return false;
      }
      return true;
    }

    // Early return here if trip is cancelled. If it wasn't on the current
    // page, we can return false.
    if (
      tripLegEventType === TripLegEventType.TripLegCanceled ||
      tripLegEventType === TripLegEventType.TripLegCanceledLate
    ) {
      return false;
    }

    // From here we might not have all the data in this event, so we need the
    // trip leg from the allTripLegs array.
    const tripLeg = allTripLegs?.find(
      (tripLeg) =>
        tripLeg.tripLegReferenceID === tripLegEvent.tripLegReferenceID,
    );
    // If we don't have the trip leg in the allTripLegs array, we must refetch.
    if (!tripLeg) {
      return true;
    }

    // Does the current trip leg event match all the filters?
    if (!this.doesTripLegMatchFilters(tripLeg, filters)) {
      return false;
    }

    // If the filters include status BCs, we need to check that the detailType
    // matches and then check if the trip leg will appear on the current page.
    if (filters?.tripLegStatusBCs) {
      if (!filters.tripLegStatusBCs.includes(tripLeg.tripLegStatusBC!)) {
        return false;
      } else {
        return this.doesTripLegSortToCurrentPage(tripLeg, filters, tripLegs);
      }
    }

    // We need to check if the trip leg will appear on the current page based on
    // the event type and the sort model. If no status BCs are provided, we can
    // just check the following event types
    if (
      tripLegEventType === TripLegEventType.TripLegOpened ||
      tripLegEventType === TripLegEventType.TripLegUnderDiscussion ||
      tripLegEventType === TripLegEventType.TripLegException ||
      tripLegEventType === TripLegEventType.TripLegEscalated
    ) {
      return this.doesTripLegSortToCurrentPage(tripLeg, filters, tripLegs);
    }

    return false;
  }
}

const realtimeUtils = new RealtimeUtils();

export default realtimeUtils;
